import { useTranslation } from 'react-i18next';
import type { ConnectionStatus } from '../hooks/useWebSocket';
import { useDirection } from '../i18n/useDirection';
import { AIBadge } from './AIBadge';

export interface ChatHeaderProps {
  /** Title to display in the header */
  title: string;
  /** WebSocket connection status */
  connectionStatus: ConnectionStatus;
  /** Header background color */
  headerColor: string;
  /** Company logo URL for widget header */
  logoUrl?: string;
  /** How the header logo is shaped: square/rounded crop or a wide aspect-preserved logo */
  logoStyle?: 'square_rounded' | 'horizontal_broad';
  /** How the logo image fills its box */
  logoFit?: 'cover' | 'contain';
  /** Close button handler */
  onClose: () => void;
  /**
   * Flips the layout direction. Only passed when the club allows visitors to choose
   * (`layout.allow_direction_change`); otherwise the control is not rendered at all.
   */
  onToggleDirection?: () => void;
  /** Pad the top by the device safe-area inset (fullscreen on notched phones). */
  safeAreaTop?: boolean;
}

/**
 * Chat window header component.
 *
 * Features:
 * - Displays router title
 * - Connection status indicator dot (green/yellow/red)
 * - Close button (X)
 * - Customizable header background color
 */
export function ChatHeader({
  title,
  connectionStatus,
  headerColor,
  logoUrl,
  logoStyle = 'square_rounded',
  logoFit = 'cover',
  onClose,
  onToggleDirection,
  safeAreaTop = false,
}: ChatHeaderProps) {
  const { t } = useTranslation('widget');
  const { isRTL } = useDirection();
  const fitClass = logoFit === 'contain' ? 'object-contain' : 'object-cover';
  // Horizontal logos render aspect-preserved and wide (no round crop / ring);
  // square logos keep the original 36px circle.
  const logoClassName =
    logoStyle === 'horizontal_broad'
      ? `h-9 w-auto max-w-[160px] rounded-md ${fitClass} shrink-0`
      : `w-9 h-9 rounded-full ${fitClass} border border-white/30 shrink-0`;
  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-400';
      case 'connecting':
      case 'reconnecting':
        return 'bg-yellow-400 animate-pulse';
      case 'disconnected':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusTitle = () => {
    switch (connectionStatus) {
      case 'connected':
        return t('header.connected');
      case 'connecting':
        return t('header.connecting');
      case 'reconnecting':
        return t('header.reconnecting');
      case 'disconnected':
        return t('header.disconnected');
      default:
        return t('header.unknown');
    }
  };

  return (
    // No `row-reverse` on this row: the widget root carries `dir`, which already lays a flex row
    // out from the reading side. Reversing it again cancelled that out and left the close button on
    // the right in a mirrored chat, which is the one place it must not be.
    <div
      className="text-white px-4 py-3 flex items-center justify-between shrink-0"
      style={{
        backgroundColor: headerColor,
        ...(safeAreaTop ? { paddingTop: 'calc(12px + env(safe-area-inset-top))' } : {}),
      }}
    >
      <div className="flex items-center gap-2">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${title} logo`}
            className={logoClassName}
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-white/20 shrink-0" aria-hidden="true" />
        )}
        <div className="flex flex-col gap-0.5 items-start">
          <AIBadge size="sm" />
          <div className="flex items-center gap-2">
            {/* The club's own name: `auto` keeps a Latin name reading left to right inside a
                mirrored header, rather than dragging its punctuation to the wrong end. */}
            <span className="font-semibold" dir="auto">{title}</span>
            <span
              className={`w-2 h-2 rounded-full ${getStatusColor()}`}
              title={getStatusTitle()}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {/* Sits before the close button, so it lands inboard of it in either direction. */}
        {onToggleDirection && (
          <button
            onClick={onToggleDirection}
            className="p-1 rounded hover:bg-white/20 transition-colors"
            aria-label={t('header.toggleDirection', {
              defaultValue: 'Switch between left-to-right and right-to-left layout',
            })}
            title={t('header.toggleDirection', {
              defaultValue: 'Switch between left-to-right and right-to-left layout',
            })}
            aria-pressed={isRTL}
            type="button"
          >
            <DirectionIcon mirrored={isRTL} />
          </button>
        )}
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-white/20 transition-colors"
          aria-label={t('header.closeChat')}
          type="button"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}

/**
 * Layout-direction icon: a paragraph with an arrow showing which way the chat is laid out. Mirrors
 * with the current direction, so it reads as "this is the way round you are now".
 */
function DirectionIcon({ mirrored }: { mirrored: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
      data-mirrored={mirrored ? 'true' : undefined}
      style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
    >
      {/* Three lines of text, ragged on the end side... */}
      <path d="M3 5h14M3 9h10M3 13h12" />
      {/* ...and an arrow pointing the way it reads. */}
      <path d="M3 17h9m0 0-2.5-2.5M12 17l-2.5 2.5" />
    </svg>
  );
}

/**
 * Close icon SVG component.
 */
function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

export default ChatHeader;
